import React, { forwardRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../style/colors';
import { ITheme } from '../../../style/theme';

const useStyles = makeStyles((theme: ITheme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputContainer: ({
    disabled,
    error,
    type,
  }: {
    disabled: boolean;
    error: any;
    type: any;
  }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderColor: colors.darkestGray,
    '&:focus-within': {
      borderColor: colors.gainsboro,
    },
    ...(disabled && {
      borderColor: colors.darkGray,
    }),
    ...(error && {
      borderColor: colors.darkestBlue,
    }),
    ...(type === 'tel' && {
      borderBottomWidth: 0,
      borderBottomStyle: 'none',
    }),
  }),
  input: ({ disabled }: { disabled: boolean }) => ({
    flex: 1,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(2),
    },
    border: 0,
    borderRadius: 0,
    outline: 'none',
    backgroundColor: 'transparent',
    color: colors.darkestGray,
    '&::placeholder': {
      color: colors.lightGray,
    },
    ...(disabled && {
      pointerEvents: 'none',
      color: colors.darkestGray,
    }),
  }),
  label: ({ disabled }) => ({
    textTransform: 'uppercase',
    ...(disabled && {
      color: colors.lightGray,
    }),
  }),
  optionalLabel: ({ disabled }) => ({
    textTransform: 'none',
    ...(disabled && {
      color: colors.lightGray,
    }),
  }),
  error: {
    marginTop: theme.spacing(0.5),
  },
}));

const Input = forwardRef((props, ref) => {
  // @ts-ignore next
  const { id, error, disabled, icon, label, required, ...rest } = props;

  const classes = useStyles({
    // @ts-ignore next
    type: rest.type,
    disabled,
    error,
  });

  const { t } = useTranslation();

  return (
    <div className={classes.root}>
      {label && (
        <Typography
          component="label"
          variant="body2"
          className={classes.label}
          htmlFor={id}
          color={error ? 'error' : 'initial'}
        >
          {label}
          {!required && (
            <Typography
              className={classes.optionalLabel}
              variant="body2"
              component="span"
            >
              {` (${t('Shared.optional')})`}
            </Typography>
          )}
        </Typography>
      )}
      <div className={classes.inputContainer}>
        <Typography
          component="input"
          variant="body1"
          id={id}
          className={classes.input}
          disabled={disabled}
          // @ts-ignore next
          ref={ref}
          {...rest}
        />
        {icon}
      </div>
      {error && (
        <Typography
          component="p"
          variant="body2"
          color="error"
          className={classes.error}
        >
          {t(error.message)}
        </Typography>
      )}
    </div>
  );
});

Input.defaultProps = {
  // @ts-ignore next
  component: 'input',
  type: 'text',
  label: '',
  required: false,
  disabled: false,
  icon: null,
  error: null,
};

export default Input;
